import { BillingCadenceOrder } from '@fruitful/types';
import { useAtomValue } from 'jotai';
import React, { useState } from 'react';
import type {
    NativeSyntheticEvent,
    TextInputSubmitEditingEventData,
} from 'react-native';
import {
    BackButton,
    BackButtonIconType,
    BodyMediumEmphasized,
    BodyMediumRegular,
    DatePicker,
    isWeb,
    KeyboardCompatibleBottomSheetTextInput,
    ScrollView,
    StyledH2Input,
    YStack,
} from 'smoothie';

import {
    getDayFromOrdinalNumber,
    getOrdinalSuffix,
} from '../../../common/misc';
import {
    getCadenceDisplayString,
    getCategoryDisplayString,
    getDayOfWeekString,
    getLastPaidDate,
    sortBillsAccounts,
    sortCategories,
} from '../../../common/utils/billUtils';
import { captureWarning } from '../../../logger/Logger';
import { billsAccountsAtom } from '../../state/BillsAtoms';
import {
    BillCategory,
    BillingCadence,
    type PartialBill,
} from '../../types/Bill';
import { BillIcon, BillIconSize } from '../common/BillIcons';
import { BillAccountRow, SelectableRow } from '../common/BillRows';

const SheetHeader = ({
    title,
    onBack,
}: {
    title: string;
    onBack: () => void;
}) => {
    return (
        <YStack width="100%" paddingTop={10} paddingBottom={20}>
            <YStack alignItems="center">
                <BodyMediumEmphasized>{title}</BodyMediumEmphasized>
            </YStack>
            <YStack marginTop={-32}>
                <BackButton
                    customBackFn={onBack}
                    iconType={BackButtonIconType.ARROW_LEFT}
                />
            </YStack>
        </YStack>
    );
};

interface DetailInputProps {
    bill: PartialBill;
    onSubmit: (updatedBill: PartialBill) => void;
    onBack: () => void;
}

export const AccountInput = ({ bill, onSubmit, onBack }: DetailInputProps) => {
    const billsAccounts = useAtomValue(billsAccountsAtom);

    if (!billsAccounts.length) {
        captureWarning('No bills-eligible accounts found');
        return (
            <YStack>
                <BodyMediumRegular>No spend accounts found</BodyMediumRegular>
            </YStack>
        );
    }

    const sortedBillAccounts = sortBillsAccounts(billsAccounts);

    return (
        <YStack flex={1} justifyContent="space-between">
            <SheetHeader title="Pick the account to pay from" onBack={onBack} />
            <ScrollView height="35%">
                <YStack>
                    {sortedBillAccounts?.map((account, i) => {
                        return (
                            <SelectableRow
                                key={account.id}
                                isSelected={account.id === bill.accountId}
                                isMultiSelect={false}
                                isFirstRow={i === 0}
                                onSelect={() => {
                                    bill.accountId = account.id;
                                    onSubmit(bill);
                                }}
                                rowContentComponent={
                                    <BillAccountRow
                                        account={account}
                                        viewType="account_selection"
                                    />
                                }
                            />
                        );
                    })}
                </YStack>
            </ScrollView>
        </YStack>
    );
};

export const DisplayNameInput = ({
    bill,
    onSubmit,
    onBack,
}: DetailInputProps) => {
    const { icon, color, displayName, payee } = bill;
    const [billDisplayName, setBillDisplayName] = useState<string>(
        displayName ?? payee ?? '',
    );

    return (
        <YStack flex={1} alignItems="center">
            <SheetHeader title="What is this for?" onBack={onBack} />
            <BillIcon
                icon={icon}
                color={color}
                size={BillIconSize.XL}
                hasShadow={true}
            />
            <KeyboardCompatibleBottomSheetTextInput
                component={StyledH2Input}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus={!isWeb} // If autofocus is on for web, it messes up the TabView index
                editable
                enablesReturnKeyAutomatically
                placeholder="Name"
                autoComplete="off"
                inputMode="text"
                keyboardType="default"
                spellCheck={true}
                maxLength={40}
                textAlign="center"
                padding={10}
                marginTop={16}
                height="auto"
                enterKeyHint="next"
                returnKeyType="next"
                value={billDisplayName}
                onChangeText={setBillDisplayName}
                onSubmitEditing={(
                    event: NativeSyntheticEvent<TextInputSubmitEditingEventData>,
                ) => {
                    // Only set displayName if not empty, otherwise set to undefined
                    const trimmedValue = event.nativeEvent.text.trim();
                    bill.displayName =
                        trimmedValue !== '' ? trimmedValue : undefined;
                    onSubmit(bill);
                }}
            />
        </YStack>
    );
};

export const PayeeInput = ({ bill, onSubmit, onBack }: DetailInputProps) => {
    const { payee } = bill;
    const [billPayee, setBillPayee] = useState<string>(payee ?? '');

    return (
        <YStack flex={1} alignItems="center">
            <SheetHeader title="Who do you pay this to?" onBack={onBack} />
            <KeyboardCompatibleBottomSheetTextInput
                component={StyledH2Input}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus={!isWeb} // If autofocus is on for web, it messes up the TabView index
                editable
                enablesReturnKeyAutomatically
                placeholder="Payee"
                autoComplete="off"
                inputMode="text"
                keyboardType="default"
                spellCheck={true}
                maxLength={40}
                textAlign="center"
                padding={10}
                marginTop={0}
                height="auto"
                width="100%"
                enterKeyHint="next"
                returnKeyType="next"
                value={billPayee}
                onChangeText={setBillPayee}
                onSubmitEditing={(
                    event: NativeSyntheticEvent<TextInputSubmitEditingEventData>,
                ) => {
                    // Require payee to have a value
                    const trimmedValue = event.nativeEvent.text.trim();
                    if (trimmedValue !== '') {
                        bill.payee = trimmedValue;
                        onSubmit(bill);
                    }
                }}
            />
        </YStack>
    );
};

export const CadenceInput = ({ bill, onSubmit, onBack }: DetailInputProps) => {
    return (
        <YStack flex={1} justifyContent="space-between">
            <SheetHeader title="How frequent is this bill?" onBack={onBack} />
            <ScrollView height="35%">
                <YStack>
                    {Object.values(BillingCadence)
                        .filter(
                            (cadence) =>
                                cadence !== BillingCadence.UNKNOWN &&
                                cadence !== BillingCadence.YEARLY,
                        )
                        .sort((a, b) => {
                            return (
                                BillingCadenceOrder[a] - BillingCadenceOrder[b]
                            );
                        })
                        .map((cadence, i) => {
                            return (
                                <SelectableRow
                                    key={i}
                                    isSelected={cadence === bill.cadence}
                                    isMultiSelect={false}
                                    isFirstRow={i === 0}
                                    onSelect={() => {
                                        bill.cadence = cadence;
                                        onSubmit(bill);
                                    }}
                                    rowContentComponent={
                                        <BodyMediumRegular padding={8}>
                                            {getCadenceDisplayString(cadence)}
                                        </BodyMediumRegular>
                                    }
                                />
                            );
                        })}
                </YStack>
            </ScrollView>
        </YStack>
    );
};

const TODAY = new Date();
const MAX_DATE = TODAY;
const MIN_DATE = new Date(TODAY.getFullYear() - 2, 0, 1);

export const TimingInput = ({ bill, onSubmit, onBack }: DetailInputProps) => {
    const billDay = getDayFromOrdinalNumber(bill?.timingLabel);

    const onPressSubmit = (lastPaidDate: Date) => {
        const day = lastPaidDate?.getDate();
        if (bill.cadence === BillingCadence.MONTHLY) {
            bill.timingLabel = `${day}${getOrdinalSuffix(day)}`;
            bill.timingDescription = `Monthly around the ${bill.timingLabel}`;
        } else if (bill.cadence === BillingCadence.QUARTERLY) {
            bill.timingLabel = getCadenceDisplayString(bill.cadence);
            bill.timingDescription = 'Around the same time every quarter';
        } else if (bill.cadence === BillingCadence.ANNUALLY) {
            const monthStringLong = lastPaidDate.toLocaleDateString('default', {
                month: 'long',
            });
            const monthStringShort = lastPaidDate.toLocaleDateString(
                'default',
                {
                    month: 'short',
                },
            );
            bill.timingLabel = monthStringShort;
            bill.timingDescription = `Every year in ${monthStringLong}`;
        } else if (bill.cadence === BillingCadence.SEMI_ANNUALLY) {
            bill.timingLabel = `${day}${getOrdinalSuffix(day)}`;
            bill.timingDescription = `Every six months around the ${bill.timingLabel}`;
        } else if (
            bill.cadence === BillingCadence.SEMI_MONTHLY ||
            bill.cadence === BillingCadence.BI_WEEKLY
        ) {
            bill.timingLabel = `${day}${getOrdinalSuffix(day)}`;
            bill.timingDescription = `Approximately every two weeks around the ${bill.timingLabel}`;
        } else if (bill.cadence === BillingCadence.WEEKLY) {
            const dayOfWeek = lastPaidDate?.getDay();
            bill.timingLabel = getDayOfWeekString(dayOfWeek);
            bill.timingDescription = `Weekly on ${bill.timingLabel}`;
        } else if (bill.cadence === BillingCadence.DAILY) {
            bill.timingLabel = getCadenceDisplayString(bill.cadence);
            bill.timingDescription = `Daily`;
        }
        onSubmit(bill);
    };

    return (
        <YStack>
            <SheetHeader
                title="Around when was this last paid?"
                onBack={onBack}
            />
            <ScrollView width="100%">
                <YStack width="100%" alignItems="center">
                    <DatePicker
                        value={getLastPaidDate(billDay)}
                        onChange={(date) => {
                            if (date != null) {
                                onPressSubmit(date);
                            }
                        }}
                        minimumDate={MIN_DATE}
                        maximumDate={MAX_DATE}
                    />
                </YStack>
            </ScrollView>
        </YStack>
    );
};

export const CategoryInput = ({ bill, onSubmit, onBack }: DetailInputProps) => {
    return (
        <YStack flex={1} justifyContent="space-between">
            <SheetHeader title="What type of bill is this?" onBack={onBack} />
            <ScrollView>
                <YStack>
                    {Object.values(BillCategory)
                        .sort(sortCategories)
                        .map((category, i) => (
                            <SelectableRow
                                key={i}
                                isSelected={category === bill.category}
                                isMultiSelect={false}
                                isFirstRow={i === 0}
                                onSelect={() => {
                                    bill.category = category;
                                    onSubmit(bill);
                                }}
                                rowContentComponent={
                                    <BodyMediumRegular padding={8}>
                                        {getCategoryDisplayString(category)}
                                    </BodyMediumRegular>
                                }
                            />
                        ))}
                </YStack>
            </ScrollView>
        </YStack>
    );
};
